import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { Notification, NotificationStatus } from '../Entity/Notification';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL ||
  (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:5133/api/Web/'
    : 'https://api.modern.roamtravel.koushikisoftware.com/api/Web/');

/** Maps REACT_APP_API_BASE_URL (.../api/Web/) onto the SignalR hub path. */
export function resolveAdminNotificationHubUrl(baseUrl: string = apiBaseUrl): string {
    const trimmed = baseUrl.replace(/\/+$/, '');
    const root = trimmed.replace(/\/api\/(Web|AdminWeb)$/i, '');
    return `${root}/hubs/admin-notifications`;
}

function timeMs(value: string | Date | undefined): number {
    if (!value) {
        return 0;
    }
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : 0;
}

function readField<T>(raw: unknown, pascal: string, camel: string): T | undefined {
    if (!raw || typeof raw !== 'object') {
        return undefined;
    }
    const record = raw as Record<string, unknown>;
    if (record[pascal] !== undefined && record[pascal] !== null) {
        return record[pascal] as T;
    }
    return record[camel] as T | undefined;
}

export function normalizeNotification(raw: unknown): Notification {
    return {
        NotificationId: readField<string>(raw, 'NotificationId', 'notificationId') ?? '',
        SourceEntityId: readField<string>(raw, 'SourceEntityId', 'sourceEntityId') ?? '',
        SourceEntityType: readField<string>(raw, 'SourceEntityType', 'sourceEntityType') ?? '',
        TargetEntityId: readField<string>(raw, 'TargetEntityId', 'targetEntityId') ?? '',
        TargetEntityType: readField<string>(raw, 'TargetEntityType', 'targetEntityType') ?? '',
        Status: readField<Notification['Status']>(raw, 'Status', 'status') ?? NotificationStatus.Created,
        Message: readField<string>(raw, 'Message', 'message') ?? '',
        StatusChangeDate: readField<Date>(raw, 'StatusChangeDate', 'statusChangeDate') ?? new Date(0),
        CreateDate: readField<Date>(raw, 'CreateDate', 'createDate') ?? new Date(0),
        CreateBy: readField<string>(raw, 'CreateBy', 'createBy') ?? '',
        TimeAgo: readField<string>(raw, 'TimeAgo', 'timeAgo') || '',
    };
}

/**
 * Merge a SignalR batch into the bell list.
 * Existing rows are updated in place. Unknown rows are inserted only when they
 * are at least as new as the newest loaded row, so an older page is not pulled
 * into the top of the list.
 */
export function applyNotificationBatch(current: Notification[], incoming: unknown[]): Notification[] {
    const next = new Map(current.map(item => [item.NotificationId, item]));
    let newestMs = current.reduce((max, item) => Math.max(max, timeMs(item.CreateDate)), Number.NEGATIVE_INFINITY);

    incoming.forEach(raw => {
        const item = normalizeNotification(raw);
        if (!item.NotificationId) {
            return;
        }

        if (next.has(item.NotificationId)) {
            next.set(item.NotificationId, item);
            newestMs = Math.max(newestMs, timeMs(item.CreateDate));
            return;
        }

        const createdMs = timeMs(item.CreateDate);
        if (current.length === 0 || createdMs >= newestMs) {
            next.set(item.NotificationId, item);
            newestMs = Math.max(newestMs, createdMs);
        }
    });

    return Array.from(next.values()).sort((a, b) => timeMs(b.CreateDate) - timeMs(a.CreateDate));
}

export function appendOlderNotifications(current: Notification[], older: Notification[]): Notification[] {
    const ids = new Set(current.map(item => item.NotificationId));
    const merged = current.concat(
        older
            .map(normalizeNotification)
            .filter(item => item.NotificationId && !ids.has(item.NotificationId)),
    );
    return merged.sort((a, b) => timeMs(b.CreateDate) - timeMs(a.CreateDate));
}

export function startAdminNotificationHub(
    accessTokenFactory: () => string,
    onBatch: (items: Notification[]) => void,
): HubConnection {
    const connection = new HubConnectionBuilder()
        .withUrl(resolveAdminNotificationHubUrl(), {
            accessTokenFactory,
            // Token auth only; the API's CORS policy allows any origin without credentials.
            withCredentials: false,
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();

    connection.on('NotificationsChanged', (items: Notification[] | Notification) => {
        const batch = Array.isArray(items) ? items : [items];
        onBatch(batch.map(normalizeNotification));
    });

    return connection;
}
